import axios from "axios";
import _ from "lodash";
import moment from "moment";
import {axiosCatchHandler} from "../sharedGlobal";
import {captureException} from "../../errorHelper";

const dashboard = {
  namespaced: true,
  state: {
    uuid: null,
    activeWebsite: null,
    preview: false,

    gmbLocations: [],
    countryFlags: [],
    layout: [],
    customWidgets: [],

    addWidgetModal: false,
    selectedOptionIndex: 0,
    customWidgetModal: false,
    myWidgetsModal: false,
    chooseCompetitorsModal: false,
    chooseLocationModal: false,
    chooseLocalAverageTypeModal: false,
    editMyWidget: null,
    selectedOption: null,

    trafficData: {},
    trafficDataError: false,
    chartBaseOptions: {
      chart: {
        width: '100%',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth'
      },
      title: {
        show: false
      },
      noData: {
        text: 'No Data',
        style: {
          fontSize: '25px',
        }
      },
    },

    blogs: [],
  },
  getters: {
    blogs: ({ blogs }) => blogs,
    activeWebsite: ({ activeWebsite }) => activeWebsite,
  },
  mutations: {
    setPreview(state, payload) {
      state.preview = payload;
    },
    async setActiveWebsite(state, website) {
      state.activeWebsite = website;
      state.uuid = website.uuid.split('_').pop();
    },
    async setGMBLocations(state, {data}) {
      state.gmbLocations = data;
    },
    async setLayout(state, {layout}){
      state.layout = layout;
    },
    async setCustomWidgets(state, {widgets}){
      state.customWidgets = widgets;
    },
    async setTrafficData(state, {data}){
      state.trafficData = data;
    },
    async setTrafficDataError(state, bool){
      state.trafficDataError = bool;
    },
    async setSelectedOption(state, data){
      state.selectedOption = data;
    },
    async clearSelectedOption(state){
      state.selectedOption = null;
    },
    async setSelectedOptionIndex(state, index){
      state.selectedOptionIndex = index;
    },
    setBlogs(state, blogs) {
      state.blogs = blogs;
    }
  },
  actions: {
    async openAddWidgetModal({state}) {
      state.addWidgetModal = true;
    },
    async closeAddWidgetModal({state}) {
      state.addWidgetModal = false;
    },
    async openCustomWidgetModal({state}) {
      state.customWidgetModal = true;
    },
    async closeCustomWidgetModal({state}) {
      state.customWidgetModal = false;
      state.editMyWidget = null;
    },
    async openMyWidgetsModal({state}) {
      state.myWidgetsModal = true;
    },
    async editCustomWidget({state}, widget) {
      state.editMyWidget = widget;
      state.myWidgetsModal = false;
      state.customWidgetModal = true;
    },
    async closeMyWidgetsModal({state}) {
      state.myWidgetsModal = false;
    },
    async openChooseCompetitorsModal({state}) {
      state.chooseCompetitorsModal = true;
    },
    async closeChooseCompetitorsModal({state}) {
      state.chooseCompetitorsModal = false;
    },
    async openChooseLocationModal({state}) {
      state.chooseLocationModal = true;
    },
    async closeChooseLocationModal({state}) {
      state.chooseLocationModal = false;
    },
    async openChooseLocalAverageTypeModal({state}) {
      state.chooseLocalAverageTypeModal = true;
    },
    async closeChooseLocalAverageTypeModal({state}) {
      state.chooseLocalAverageTypeModal = false;
    },

    getActiveWebsiteUUID: ({commit}, uuid) =>
      axios.post(`/ajax/dashboard/get-active-website-uuid`, {uuid})
        .then(response => commit('setActiveWebsite', response.data)),
    getGMBLocations: ({state, commit}) => {
      const {uuid} = state;
      return axios.post('/ajax/dashboard/get-locations', {uuid})
        .then(({data}) => commit('setGMBLocations', {data}));
    },
    getCountryFlags: ({state}) =>
      axios.get('/ajax/dashboard/get-country-flags')
        .then(({data}) => {
          state.countryFlags = data;
        }),
    getLayout: ({state, commit}) => {
      commit('setLayout', {layout: []});
      return axios.post(`/ajax/dashboard/get-layout`, {
          uuid: state.uuid
        })
        .then(({data}) => commit('setLayout', {layout: data}));
    },
    getCustomWidgets: ({state, commit}) => {
      const {uuid} = state;
      return axios.post('/ajax/dashboard/get-custom-widgets', {uuid})
        .then(({data}) => commit('setCustomWidgets', {widgets: data}));
    },
    createCustomWidget: ({dispatch}, payload) =>
      new Promise((resolve, reject) => {
          axios.post('/ajax/dashboard/create-custom-widget', payload)
            .then(() => {
              dispatch('getCustomWidgets')
              window.mixpanel('Dashboard: Create Custom Widget', {
                widget_data: payload,
              })
            })
            .catch(
              (e) => axiosCatchHandler(e, ({response}) => reject(response))
            );
        }),
    updateCustomWidget: ({dispatch}, payload) =>
      new Promise((resolve, reject) => {
        axios.post('/ajax/dashboard/update-custom-widget', payload)
          .then(() => dispatch('getCustomWidgets'))
          .catch(
            (e) => axiosCatchHandler(e, ({response}) => reject(response))
          );
      }),
    deleteCustomWidget: ({dispatch}, payload) =>
      axios.post('/ajax/dashboard/delete-custom-widget', {id: payload})
        .then(() => dispatch('getCustomWidgets')),
    async addWidget({state, commit, dispatch}, {widget}) {
      return new Promise(async (resolve, reject) => {
        try {
          delete widget.content;
          let layout = state.layout ?? [];
          layout.push(widget);
          await commit('setLayout', {layout: layout});

          // Check if grid has been loaded. If not, it suggests this is the first widget being added to the layout
          let grid = document.querySelector('#dashboard-grid').gridstack;
          if (grid) {
            let gridWidget = grid.makeWidget(`#${widget.id}`);
            grid.update(gridWidget, widget);
          }

          window.mixpanel('Dashboard: Add Dashboard Widget', {
            widget_type: widget.component,
          })
          dispatch('saveLayout');
          resolve(widget);
        } catch (e) {
          captureException(new Error('Add Widget Error'), {
            error: e
          });
          reject(e);
        }
      });
    },
    async updateWidget({state, commit, dispatch}, {widget}) {
      return new Promise(async (resolve) => {
        try {
          let layout = state.layout ?? [];
          let index = _.findIndex(layout, {id: widget.id});
          layout[index] = widget;
          await commit('setLayout', {layout: layout});

          let grid = document.querySelector('#dashboard-grid').gridstack;
          let gridWidget = document.querySelector(`#${widget.id}`);
          grid.update(gridWidget, widget);

          resolve(widget);
        } catch (e) {
          captureException(new Error('Update Widget Error'), {
            'error': e,
          });
          reject(e);
        }
      });
    },
    async removeWidget({state, commit}, widget) {
      // Fetch the Gridstack grid
      let grid = document.querySelector('#dashboard-grid').gridstack;
      // Get the widget element.
      const widgetEl = document.querySelector(`[gs-id="${widget.id}"]`);
      // Remove the widget from the grid
      grid.removeWidget(widgetEl);
      grid.compact();
    },

    async saveLayout({dispatch}) {
      await dispatch('debounceSaveLayout');
    },
    debounceSaveLayout: _.debounce(({dispatch}) => {
      dispatch('saveLayoutRequest');
    }, 2000),
    saveLayoutRequest: ({commit}) => {
      let grid = document.getElementById('dashboard-grid').gridstack;
      let layout = grid.save(false);
      return axios.post(`/ajax/dashboard/save-layout`, {layout: layout})
        .then(response => commit('setLayout', {layout: response.data}));
    },
    getTrafficData: ({commit}, websiteId) => {
      let fromDate = moment().subtract(1, 'day').subtract(1, 'month').format('YYYY-MM-DD');
      let toDate = moment().subtract(1, 'day').format('YYYY-MM-DD');
      let compFromDate = moment(fromDate).subtract(1, 'month').format('YYYY-MM-DD');
      let compToDate = moment(toDate).subtract(1, 'month').format('YYYY-MM-DD');
      return axios.post(
          `/ajax/dashboard/get-ga-data/${websiteId}?fromDate=${fromDate}&toDate=${toDate}&compFromDate=`+
          `${compFromDate}&compToDate=${compToDate}`
        )
        .then(({data}) => commit('setTrafficData', {data}))
        .catch(
          (e) => axiosCatchHandler(e, () => commit('setTrafficDataError', true))
        );
    },
    getBlogs: ({commit}) =>
      axios.get('ajax/dashboard/get-blogs')
        .then(({data}) => commit('setBlogs', data)),
  }
};

export default dashboard;
